import type { Account } from "../types";
import { statusInfo, pct, money } from "../format";
import { windowedStats, WINDOW_LABEL, type TfWindow, type WindowedStats } from "../perf";

const fmtPf = (v: number) => (v === Infinity ? "∞" : isFinite(v) ? v.toFixed(2) : "—");
const fmtRet = (v: number) => `${v >= 0 ? "+" : ""}${(v * 100).toFixed(1)}%`;

interface Ranked {
  acct: Account;
  ws: WindowedStats;
}

function PerfCard({ kind, r, onOpen }: { kind: "top" | "worst"; r: Ranked; onOpen: (id: string) => void }) {
  const { acct, ws } = r;
  const s = statusInfo(acct.status);
  return (
    <article className={`card perf-card perf-${kind}`} aria-labelledby={`perf-${kind}-${acct.account_id}`}>
      <header className="perf-card-head">
        <span className="perf-card-kind">{kind === "top" ? "Top performer" : "Worst performer"}</span>
        <span className={s.cls}><span aria-label={s.sr}>{s.label}</span></span>
      </header>
      <button
        id={`perf-${kind}-${acct.account_id}`}
        className="perf-card-title linklike"
        onClick={() => onOpen(acct.account_id)}
      >
        {acct.instrument} <span className="muted">· {acct.tf}</span>
      </button>
      <div className={`perf-return ${ws.return >= 0 ? "win" : "loss"}`}>{fmtRet(ws.return)}</div>
      <dl className="perf-stats">
        <div><dt>PF</dt><dd>{fmtPf(ws.pf)}</dd></div>
        <div><dt>Win rate</dt><dd>{pct(ws.wr)}</dd></div>
        <div><dt>P&amp;L</dt><dd>{money(ws.pnl)}</dd></div>
        <div><dt>Trades</dt><dd>{ws.nTrades}</dd></div>
      </dl>
    </article>
  );
}

export default function OverviewPerf({
  accounts,
  window,
  onOpen,
}: {
  accounts: Account[];
  window: TfWindow;
  onOpen: (id: string) => void;
}) {
  const ranked: Ranked[] = accounts
    .map((acct) => ({ acct, ws: windowedStats(acct, window) }))
    .sort((a, b) => b.ws.return - a.ws.return);

  if (ranked.length === 0) {
    return <p className="muted">No strategies to rank yet.</p>;
  }

  const top = ranked[0];
  const worst = ranked[ranked.length - 1];

  return (
    <>
      {top && worst && top.acct.account_id !== worst.acct.account_id && (
        <div className="perf-cards">
          <PerfCard kind="top" r={top} onOpen={onOpen} />
          <PerfCard kind="worst" r={worst} onOpen={onOpen} />
        </div>
      )}

      <h2>Leaderboard <span className="muted small">— ranked by return over {WINDOW_LABEL[window]}</span></h2>
      <div className="table-wrap" role="region" aria-label={`Leaderboard by return over ${WINDOW_LABEL[window]}`} tabIndex={0}>
        <table>
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Instrument</th>
              <th scope="col">Return</th>
              <th scope="col">PF</th>
              <th scope="col">Win rate</th>
              <th scope="col">Trades</th>
              <th scope="col">Status</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {ranked.map((r, i) => {
              const s = statusInfo(r.acct.status);
              return (
                <tr key={r.acct.account_id}>
                  <td>{i + 1}</td>
                  <th scope="row">{r.acct.instrument} <span className="muted">· {r.acct.tf}</span></th>
                  <td className={r.ws.return >= 0 ? "win" : "loss"}>{fmtRet(r.ws.return)}</td>
                  <td>{fmtPf(r.ws.pf)}</td>
                  <td>{pct(r.ws.wr)}</td>
                  <td>{r.ws.nTrades}</td>
                  <td><span className={s.cls}><span aria-label={s.sr}>{s.label}</span></span></td>
                  <td>
                    <button className="linklike" onClick={() => onOpen(r.acct.account_id)}>View →</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
